import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, CheckCircle, Zap, Shield, Users, Star,
  ShoppingCart, DollarSign, Megaphone, UserCheck, TrendingUp, Settings, Briefcase
} from "lucide-react";

const FUNCTIONS = [
  { icon: ShoppingCart, name: "Procurement",  color: "blue",   desc: "Vendor analysis, negotiations, contracts" },
  { icon: DollarSign,   name: "Finance",      color: "green",  desc: "Financial models, cost analysis" },
  { icon: Megaphone,    name: "Marketing",    color: "purple", desc: "Campaign strategy, content briefs" },
  { icon: UserCheck,    name: "HR",           color: "orange", desc: "Job descriptions, interview kits" },
  { icon: TrendingUp,   name: "Sales",        color: "red",    desc: "Proposals, objection handling" },
  { icon: Settings,     name: "Operations",   color: "teal",   desc: "Process optimization, efficiency" },
  { icon: Briefcase,    name: "General",      color: "indigo", desc: "Emails, meeting minutes, business cases" },
];

const COLOR_MAP = {
  blue:   "bg-blue-100 text-blue-700",
  green:  "bg-emerald-100 text-emerald-700",
  purple: "bg-purple-100 text-purple-700",
  orange: "bg-orange-100 text-orange-700",
  red:    "bg-red-100 text-red-700",
  teal:   "bg-teal-100 text-teal-700",
  indigo: "bg-indigo-100 text-indigo-700",
};

const FEATURES = [
  { icon: Zap,        title: "Ready-to-Use Prompts",    desc: "Structured AI applications ready to copy and use immediately in your workflow." },
  { icon: Shield,     title: "Enterprise-Grade Quality", desc: "Each application is built by professionals for real business outcomes." },
  { icon: Users,      title: "Built for Teams",          desc: "From individual contributors to enterprise teams — scale as you grow." },
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="gradient-hero py-20 lg:py-32 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 bg-blue-100 text-blue-700 border-0 px-4 py-1.5 text-sm font-medium">
            AI Applications for Business Professionals
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Use AI Applications to
            <span className="text-blue-600 block">Get Work Done Faster</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            A curated library of structured AI prompts for every business function. 
            Built for professionals, consultants, and enterprise teams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate("/auth?tab=register")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-base font-semibold"
              data-testid="hero-get-started-btn"
            >
              Start Free <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-3 text-base"
              data-testid="hero-browse-btn"
            >
              Browse Applications
            </Button>
          </div>
          <p className="mt-4 text-sm text-slate-500">No credit card required · Free plan available</p>
        </div>
      </section>

      {/* Business Functions */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              7 Business Functions. Hundreds of Applications.
            </h2>
            <p className="text-slate-600 text-base max-w-xl mx-auto">
              Find the right AI application for your exact business challenge.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {FUNCTIONS.map((fn, i) => {
              const Icon = fn.icon;
              const colorClass = COLOR_MAP[fn.color];
              return (
                <div
                  key={fn.name}
                  className={`p-5 rounded-xl border border-slate-100 bg-white hover-lift cursor-pointer card-shadow slide-up stagger-${Math.min(i + 1, 4)}`}
                  onClick={() => navigate("/dashboard")}
                  data-testid={`function-card-${fn.name.toLowerCase()}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClass}`}>
                    <Icon size={20} />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm mb-1">{fn.name}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{fn.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Built for Professionals and Enterprises
            </h2>
            <p className="text-slate-600 text-base max-w-xl mx-auto">
              Not a prompt-sharing hobby site. A professional productivity engine.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {FEATURES.map(f => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-white rounded-xl p-6 card-shadow">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon size={20} className="text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">How It Works</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Browse by Function", desc: "Choose your business function and find the right AI application for your challenge." },
              { step: "02", title: "Copy the Prompt",    desc: "Each application includes a structured, ready-to-use prompt. Copy it in one click." },
              { step: "03", title: "Paste & Execute",    desc: "Paste the prompt into ChatGPT, Claude, or any AI tool and get professional output instantly." },
            ].map(s => (
              <div key={s.step} className="text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                  {s.step}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-20 px-4 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            From Free to Enterprise
          </h2>
          <p className="text-blue-100 text-base mb-8 max-w-xl mx-auto">
            Start free and upgrade as your needs grow. Enterprise plans available for teams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-blue-700 hover:text-white bg-transparent px-8"
              onClick={() => navigate("/pricing")}
              data-testid="view-pricing-btn"
            >
              View Pricing
            </Button>
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 font-semibold"
              onClick={() => navigate("/auth?tab=register")}
              data-testid="start-free-btn"
            >
              Start Free Today
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div style={{width:'130px', height:'34px', overflow:'hidden', position:'relative'}}>
                <img
                  src="https://customer-assets.emergentagent.com/job_workpro-ai/artifacts/aayaszwu_imresizer-Promptlee.png"
                  alt="Promptlee"
                  style={{width:'175px', position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)', filter:'brightness(0) invert(1)'}}
                />
              </div>
            </div>
            <div className="flex gap-6 text-sm">
              <button onClick={() => navigate("/pricing")} className="hover:text-white transition-colors">Pricing</button>
              <button onClick={() => navigate("/contribute")} className="hover:text-white transition-colors">Contribute</button>
              <button onClick={() => navigate("/pricing")} className="hover:text-white transition-colors">Enterprise</button>
            </div>
            <p className="text-sm">© 2024 Promptlee. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
